import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip, X } from "lucide-react";
import {
  Contracts,
  MAX_CONTRACT_BYTES,
  type ContractKind,
  type ContractStatus,
} from "@/api/contracts";
import { Accounts, Deals } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { fileSize } from "@/api/demos";
import { toast } from "@/lib/feedback";
import { KINDS, PROGRESS, STATUS } from "@/lib/contracts";
import { Modal } from "@/components/demos/DemoDialogs";

/** Start a contract: what it is, who it's with, and (optionally) the first draft. */
export default function NewContractDialog({
  onClose,
  accountId: presetAccount,
  dealId: presetDeal,
}: {
  onClose: () => void;
  accountId?: string | null;
  dealId?: string | null;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<ContractKind>("msa");
  const [status, setStatus] = useState<ContractStatus>("draft");
  const [accountId, setAccountId] = useState(presetAccount ?? "");
  const [dealId, setDealId] = useState(presetDeal ?? "");
  const [value, setValue] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => Accounts.list() });
  const dealsQuery = useQuery({ queryKey: ["deals"], queryFn: () => Deals.list() });
  const deals = (dealsQuery.data ?? []).filter((d) => !accountId || d.account_id === accountId);

  const create = useMutation({
    mutationFn: async () => {
      const c = await Contracts.create({
        title: title.trim(),
        kind,
        status,
        account_id: accountId || null,
        deal_id: dealId || null,
        value_eur: value ? Number(value) : null,
      });
      if (file) await Contracts.uploadVersion(c.id, file, "First draft");
      return c;
    },
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.ok("Contract created");
      onClose();
      navigate(`/contracts/${c.id}`);
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  return (
    <Modal title="New contract" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="contract-title">
            Title*
          </label>
          <input
            id="contract-title"
            className="input"
            autoFocus
            placeholder="e.g. AVAX S.A. Master Services Agreement"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="contract-kind">
              Type
            </label>
            <select
              id="contract-kind"
              className="input"
              value={kind}
              onChange={(e) => setKind(e.target.value as ContractKind)}
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="contract-status">
              Where it stands
            </label>
            <select
              id="contract-status"
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContractStatus)}
            >
              {PROGRESS.map((s) => (
                <option key={s} value={s}>
                  {STATUS[s].label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="contract-account">
              Account
            </label>
            <select
              id="contract-account"
              className="input"
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setDealId("");
              }}
            >
              <option value="">No account</option>
              {(accountsQuery.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="contract-deal">
              Deal
            </label>
            <select
              id="contract-deal"
              className="input"
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
            >
              <option value="">No deal</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.lead}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="contract-value">
              Value (€)
            </label>
            <input
              id="contract-value"
              className="input"
              inputMode="decimal"
              placeholder="Total contract value"
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ""))}
            />
          </div>
          <div>
            <span className="label">First draft</span>
            {file ? (
              <div className="flex items-center gap-2 rounded-xl border border-fg/[0.1] px-3 py-2 text-sm">
                <Paperclip className="h-3.5 w-3.5 shrink-0 text-mut-500" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="text-mut-500">{fileSize(file.size)}</span>
                <button aria-label="Remove file" onClick={() => setFile(null)}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button className="btn w-full" onClick={() => fileRef.current?.click()}>
                <Paperclip className="h-3.5 w-3.5" /> Attach a file
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                if (f.size > MAX_CONTRACT_BYTES) toast.err("Contract files can be up to 50 MB");
                else setFile(f);
              }}
            />
          </div>
        </div>
        <p className="text-sm text-mut-500">
          Dates, notice period and key terms can be filled in on the contract page.
          {dealId && " It will be visible to everyone who can see the deal."}
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create contract"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
